import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../utils/api';
import { API_URLS } from '../constants';

interface Version {
  id: string;
  version_number: number;
  title: string;
  description: string;
  full_text: string;
  change_notes: string;
  created_at: string;
  created_by_name: string;
}

interface VersionHistoryDisplayProps {
  promptId: string;
  isVisible: boolean;
}

const VersionHistoryDisplay: React.FC<VersionHistoryDisplayProps> = ({ promptId, isVisible }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchVersions = useCallback(async () => {
    if (!promptId || fetched) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(API_URLS.PROMPTS.VERSIONS.LIST(promptId));
      
      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }
      
      const data = await response.json();
      setVersions(data);
      setFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch versions');
    } finally {
      setLoading(false);
    }
  }, [promptId, fetched]);

  useEffect(() => {
    if (isVisible && !fetched) {
      fetchVersions();
    }
  }, [isVisible, fetched, fetchVersions]);

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-electric-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center py-4">
        Error loading versions: {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Version History</h3>
      {versions.length === 0 ? (
        <p className="text-gray-500 text-center py-4">No versions found.</p>
      ) : (
        <div className="space-y-4">
          {versions.map((version) => (
            <div key={version.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">
                    Version {version.version_number}: {version.title}
                  </h4>
                  <p className="text-sm text-gray-500">
                    By {version.created_by_name} on {new Date(version.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {version.change_notes && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Changes:</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    {version.change_notes}
                  </p>
                </div>
              )}
              
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                <p className="text-sm text-gray-600">{version.description}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Full Text:</p>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded max-h-32 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-mono text-xs">
                    {version.full_text}
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VersionHistoryDisplay;